import { useSelector } from 'react-redux';
import TableContainer from '@mui/material/TableContainer';
import EditIcon from '@mui/icons-material/EditRounded';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Table, getPaginationLabels, HintedHeader, ToggleCell } from '@components/Table';
import { Typography } from '@components/Typography';
import ActionIconButton from '@components/ActionIconButton';
import { formatAssignedEntities } from '@helpers/formatEntitiesToString';
import { useIntlLocale, useTranslation } from '@hooks';
import {
    AUTOMATION_EVENT_TRIGGERS,
    AUTOMATION_INTEGRATION_TRIGGERS,
    AUTOMATION_STATUS,
    AUTOMATION_TRIGGER_OPTIONS,
    eventTriggerId,
    expandTriggerIds,
    integrationTriggerId,
} from '@constants/automations';
import { describeDateTrigger } from './Editor/dateTrigger';

const getTableHeader = t => [
    {
        key: 0,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('common.title')}
            </Typography>
        ),
    },
    {
        key: 1,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('CampaignsPage.triggerEvent')}
            </Typography>
        ),
    },
    {
        key: 3,
        width: 70,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('AutomationsPage.table.steps')}
            </Typography>
        ),
    },
    {
        key: 4,
        width: 120,
        align: 'left',
        component: (
            <HintedHeader
                compact
                label={t('AutomationsPage.table.enrolled')}
                hint={t('AutomationsPage.table.enrolledHint')}
            />
        ),
    },
    {
        key: 5,
        width: 110,
        align: 'center',
        component: (
            <Typography align="center" variant="subtitle2">
                {t('common.enabled')}
            </Typography>
        ),
    },
    { key: 6, width: 120, component: <Typography align="left" variant="subtitle2" /> },
];

const getTableBody = (
    automations,
    canWrite,
    t,
    { onEdit, onView, onDelete, onActivate, onPause },
    togglePendingIds,
    intlLocale,
) => {
    const triggers = [
        ...AUTOMATION_TRIGGER_OPTIONS.map(option => ({
            id: option.value,
            title: t(`AutomationsPage.triggers.${option.key}`),
        })),
        ...AUTOMATION_INTEGRATION_TRIGGERS.map(option => ({
            id: integrationTriggerId(option.integration),
            title: t(`AutomationsPage.triggers.${option.key}`),
        })),
        ...AUTOMATION_EVENT_TRIGGERS.map(option => ({
            id: eventTriggerId(option.eventType),
            title: t(`AutomationsPage.triggers.${option.key}`),
        })),
    ];

    return automations.map(automation => {
        const isActive = automation.status === AUTOMATION_STATUS.ACTIVE;

        return [
            {
                component: (
                    <Typography
                        variant="subtitle2"
                        align="left"
                        sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                        {automation.title}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left" sx={{ whiteSpace: 'nowrap' }}>
                        {automation.triggerDate
                            ? describeDateTrigger(automation.triggerDate, t, intlLocale)
                            : formatAssignedEntities(
                                  triggers,
                                  expandTriggerIds(
                                      automation.triggerActivityTypes,
                                      automation.triggerIntegrationTypes,
                                      automation.triggerCustomEventType,
                                  ),
                              )}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {automation.nodeCount}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {automation.enrollmentCount}
                    </Typography>
                ),
            },
            {
                align: 'center',
                component: (
                    <ToggleCell
                        checked={isActive && !automation.isFailed}
                        disabled={!canWrite || automation.isFailed || togglePendingIds.includes(automation.id)}
                        onChange={event => (event.target.checked ? onActivate(automation) : onPause(automation))}
                        hint={automation.isFailed ? t('AutomationsPage.table.failedFixHint') : ''}
                    />
                ),
            },
            {
                component: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {canWrite ? (
                            <>
                                <ActionIconButton title={t('common.edit')} onClick={() => onEdit(automation)}>
                                    <EditIcon viewBox="0 0 22 22" />
                                </ActionIconButton>
                                <ActionIconButton title={t('common.delete')} onClick={() => onDelete(automation)}>
                                    <DeleteIcon viewBox="0 0 22 22" />
                                </ActionIconButton>
                            </>
                        ) : (
                            <ActionIconButton title={t('common.view')} onClick={() => onView(automation)}>
                                <VisibilityIcon viewBox="0 0 22 22" />
                            </ActionIconButton>
                        )}
                    </div>
                ),
            },
        ];
    });
};

const AutomationsTable = ({
    canWrite,
    page,
    rowsPerPage,
    onChangePage,
    onChangeRowsPerPage,
    onEdit,
    onView,
    onDelete,
    onActivate,
    onPause,
    togglePendingIds,
}) => {
    const { t } = useTranslation();
    const intlLocale = useIntlLocale();
    const { data, loading, totalCount, pages } = useSelector(state => state.automations);

    return (
        <TableContainer>
            <Table
                withHover
                columnNames={getTableHeader(t)}
                data={data}
                rows={getTableBody(
                    data,
                    canWrite,
                    t,
                    { onEdit, onView, onDelete, onActivate, onPause },
                    togglePendingIds,
                    intlLocale,
                )}
                onRowClick={automation => (canWrite ? onEdit(automation) : onView(automation))}
                isDataLoading={loading}
                withPagination
                paginationProps={{
                    colSpan: getTableHeader(t).length,
                    count: totalCount,
                    rowsPerPage,
                    page,
                    onPageChange: (_, newPage) => onChangePage(newPage),
                    onRowsPerPageChange: event => onChangeRowsPerPage(parseInt(event.target.value, 10)),
                    ...getPaginationLabels(t),
                    pages,
                }}
            />
        </TableContainer>
    );
};

export default AutomationsTable;
